// interlinked-tdd: exempt
// Note: "claude-desktop" / "claude-code" below are product names (Anthropic's
// CLI clients), not model identifiers — they're stable identifiers for the
// install-target client, not API model strings. The freshness-sensitive
// reference heuristic misfires here.
import { useEffect, useMemo, useReducer } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import {
  generateClaudeCode,
  generateClaudeCodeCli,
  generateClaudeDesktop,
  generateCursor,
  generateVsCode,
} from "./Install.helpers";

interface DirectoryServer {
  key: string;
  name: string;
  type: "streamable-http";
  url: string;
  description: string;
  category: string;
  defaultEnabled: boolean;
  isCurated: boolean;
}

const COPY_FEEDBACK_RESET_MS = 1600;

const COPY_STATE = {
  IDLE: "idle",
  COPIED: "copied",
  FAILED: "failed",
} as const;
type CopyState = (typeof COPY_STATE)[keyof typeof COPY_STATE];

const CATEGORY_ORDER = [
  "literature",
  "trials",
  "genomics",
  "drugs",
  "structure",
  "cancer",
  "pathways",
  "expression",
  "ontology",
  "regulatory",
  "other",
] as const;

const CATEGORY_LABEL: Record<string, string> = {
  literature: "Literature",
  trials: "Clinical trials",
  genomics: "Genomics",
  drugs: "Drug discovery",
  structure: "Structure",
  cancer: "Cancer",
  pathways: "Pathways",
  expression: "Expression",
  ontology: "Ontology",
  regulatory: "Regulatory",
  other: "Full directory",
};

type ClientId = "claude-desktop" | "claude-code" | "cursor" | "vscode";

interface ClientTab {
  id: ClientId;
  label: string;
  configPath: string;
  generate: (servers: DirectoryServer[]) => string;
  hasCli: boolean;
  notes: React.ReactNode;
}

const CLIENT_TABS: ClientTab[] = [
  {
    id: "claude-desktop",
    label: "Claude Desktop",
    configPath:
      "~/Library/Application Support/Claude/claude_desktop_config.json (macOS)\n%APPDATA%\\Claude\\claude_desktop_config.json (Windows)",
    generate: generateClaudeDesktop,
    hasCli: false,
    notes: (
      <>
        Claude Desktop&apos;s config file expects stdio servers, so we wrap each HTTP
        endpoint with{" "}
        <a
          href="https://www.npmjs.com/package/mcp-remote"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-2 hover:text-foreground"
        >
          <code>mcp-remote</code>
        </a>{" "}
        (auto-installed via <code>npx</code>; requires Node 18+). Restart Claude
        Desktop after editing. If the file already has an{" "}
        <code>mcpServers</code> block, merge the entries below into it instead
        of overwriting.
      </>
    ),
  },
  {
    id: "claude-code",
    label: "Claude Code",
    configPath:
      ".mcp.json (project root) — or run the CLI commands below for global install",
    generate: generateClaudeCode,
    hasCli: true,
    notes: (
      <>
        Claude Code speaks streamable-HTTP natively. Drop the JSON in your
        project&apos;s <code>.mcp.json</code>, or use the CLI tab to register each
        server one-shot. Run <code>claude mcp list</code> to verify.
      </>
    ),
  },
  {
    id: "cursor",
    label: "Cursor",
    configPath: "~/.cursor/mcp.json (global) — or .cursor/mcp.json (per-project)",
    generate: generateCursor,
    hasCli: false,
    notes: (
      <>
        Cursor reads MCP servers from <code>~/.cursor/mcp.json</code>. Restart
        Cursor or run <em>MCP: Refresh Servers</em> from the command palette
        after editing.
      </>
    ),
  },
  {
    id: "vscode",
    label: "VS Code",
    configPath:
      ".vscode/mcp.json (workspace) — or run MCP: Add Server from the command palette",
    generate: generateVsCode,
    hasCli: false,
    notes: (
      <>
        For GitHub Copilot in VS Code 1.95+. The top-level key is{" "}
        <code>servers</code> (not <code>mcpServers</code>). Continue users:
        their <code>config.yaml</code> uses a different schema — see{" "}
        <a
          href="https://docs.continue.dev/customize/deep-dives/mcp"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-2 hover:text-foreground"
        >
          Continue MCP docs
        </a>
        .
      </>
    ),
  },
];

interface InstallState {
  servers: DirectoryServer[];
  selected: Set<string>;
  initialized: boolean;
  loading: boolean;
  error: string | null;
  showAll: boolean;
  clientId: ClientId;
  showCli: boolean;
  copyState: CopyState;
}

type InstallAction =
  | { type: "loaded"; servers: DirectoryServer[] }
  | { type: "loadFailed"; error: string }
  | { type: "toggle"; key: string }
  | { type: "selectAll"; keys: string[] }
  | { type: "selectNone" }
  | { type: "selectDefaults" }
  | { type: "setShowAll"; value: boolean }
  | { type: "setClient"; id: ClientId }
  | { type: "setShowCli"; value: boolean }
  | { type: "setCopyState"; value: CopyState };

const initialInstallState: InstallState = {
  servers: [],
  selected: new Set(),
  initialized: false,
  loading: true,
  error: null,
  showAll: false,
  clientId: "claude-code",
  showCli: false,
  copyState: COPY_STATE.IDLE,
};

function installReducer(state: InstallState, action: InstallAction): InstallState {
  switch (action.type) {
    case "loaded": {
      const defaults = new Set(
        action.servers.filter((s) => s.defaultEnabled).map((s) => s.key),
      );
      return {
        ...state,
        servers: action.servers,
        selected: state.initialized ? state.selected : defaults,
        initialized: true,
        loading: false,
        error: null,
      };
    }
    case "loadFailed":
      return { ...state, loading: false, error: action.error };
    case "toggle": {
      const next = new Set(state.selected);
      if (next.has(action.key)) next.delete(action.key);
      else next.add(action.key);
      return { ...state, selected: next };
    }
    case "selectAll":
      return { ...state, selected: new Set(action.keys) };
    case "selectNone":
      return { ...state, selected: new Set() };
    case "selectDefaults":
      return {
        ...state,
        selected: new Set(
          state.servers.filter((s) => s.defaultEnabled).map((s) => s.key),
        ),
      };
    case "setShowAll":
      return { ...state, showAll: action.value };
    case "setClient":
      return { ...state, clientId: action.id, showCli: false };
    case "setShowCli":
      return { ...state, showCli: action.value };
    case "setCopyState":
      return { ...state, copyState: action.value };
  }
}

export function Install(): React.JSX.Element {
  const [state, dispatch] = useReducer(installReducer, initialInstallState);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/mcp-servers/all")
      .then((r) => r.json() as Promise<{ servers?: DirectoryServer[] }>)
      .then((j) => {
        if (cancelled) return;
        dispatch({
          type: "loaded",
          servers: Array.isArray(j.servers) ? j.servers : [],
        });
      })
      .catch((e) => {
        if (cancelled) return;
        dispatch({ type: "loadFailed", error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleServers = useMemo(
    () =>
      state.showAll ? state.servers : state.servers.filter((s) => s.isCurated),
    [state.servers, state.showAll],
  );

  const grouped = useMemo(() => {
    const buckets = new Map<string, DirectoryServer[]>();
    for (const s of visibleServers) {
      const cat = (CATEGORY_ORDER as readonly string[]).includes(s.category)
        ? s.category
        : "other";
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat)!.push(s);
    }
    return CATEGORY_ORDER.filter((c) => buckets.has(c)).map((c) => ({
      category: c,
      servers: (buckets.get(c) ?? []).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    }));
  }, [visibleServers]);

  const selectedServers = useMemo(
    () => state.servers.filter((s) => state.selected.has(s.key)),
    [state.servers, state.selected],
  );

  const tab = CLIENT_TABS.find((t) => t.id === state.clientId)!;
  const config = useMemo(() => {
    if (selectedServers.length === 0) return "";
    if (tab.hasCli && state.showCli) {
      return generateClaudeCodeCli(selectedServers);
    }
    return tab.generate(selectedServers);
  }, [selectedServers, tab, state.showCli]);

  const copyToClipboard = async () => {
    if (!config) return;
    try {
      await navigator.clipboard.writeText(config);
      dispatch({ type: "setCopyState", value: COPY_STATE.COPIED });
    } catch (err) {
      console.warn("Clipboard write failed; user can select text manually:", err);
      dispatch({ type: "setCopyState", value: COPY_STATE.FAILED });
    }
    setTimeout(
      () => dispatch({ type: "setCopyState", value: COPY_STATE.IDLE }),
      COPY_FEEDBACK_RESET_MS,
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <InstallHeader />

      <main className="mx-auto max-w-6xl px-6 py-12">
        <InstallIntro />

        {state.error && (
          <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            Failed to load directory: {state.error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <ServerSelector
            loading={state.loading}
            servers={state.servers}
            visibleServers={visibleServers}
            selected={state.selected}
            grouped={grouped}
            showAll={state.showAll}
            onToggle={(key) => dispatch({ type: "toggle", key })}
            onSelectAll={() =>
              dispatch({
                type: "selectAll",
                keys: visibleServers.map((s) => s.key),
              })
            }
            onSelectNone={() => dispatch({ type: "selectNone" })}
            onSelectDefaults={() => dispatch({ type: "selectDefaults" })}
            onToggleShowAll={() =>
              dispatch({ type: "setShowAll", value: !state.showAll })
            }
          />

          <ConfigOutput
            tab={tab}
            tabs={CLIENT_TABS}
            clientId={state.clientId}
            showCli={state.showCli}
            copyState={state.copyState}
            config={config}
            onSelectClient={(id) => dispatch({ type: "setClient", id })}
            onToggleCli={() =>
              dispatch({ type: "setShowCli", value: !state.showCli })
            }
            onCopy={() => {
              void copyToClipboard();
            }}
          />
        </div>

        <FaqSection />
      </main>

      <InstallFooter />
    </div>
  );
}

function InstallHeader(): React.JSX.Element {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur">
      <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="grid size-6 place-items-center rounded-md bg-foreground font-mono text-[10px] font-bold text-background">
            B
          </span>
          <span className="text-sm font-semibold tracking-tight">
            Bio MCP Portal
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:inline">
            install
          </span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link
            to="/chat"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            Open chat
          </Link>
          <a
            href="https://github.com/QuentinCody/bio-mcp-client"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            GitHub
          </a>
        </nav>
      </div>
    </header>
  );
}

function InstallIntro(): React.JSX.Element {
  return (
    <div className="mb-10 max-w-3xl">
      <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
        Install · MCP servers in your own client
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl">
        Pick your servers. Copy the config. Done.
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        Each Bio MCP server is a public, unauthenticated Cloudflare Worker
        speaking the Model Context Protocol over HTTP. Select what you want,
        choose your client, paste the snippet, restart. No accounts, no keys.
      </p>
    </div>
  );
}

interface SelectorProps {
  loading: boolean;
  servers: DirectoryServer[];
  visibleServers: DirectoryServer[];
  selected: Set<string>;
  grouped: { category: string; servers: DirectoryServer[] }[];
  showAll: boolean;
  onToggle: (key: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onSelectDefaults: () => void;
  onToggleShowAll: () => void;
}

function ServerSelector(props: SelectorProps): React.JSX.Element {
  const {
    loading,
    servers,
    visibleServers,
    selected,
    grouped,
    showAll,
    onToggle,
    onSelectAll,
    onSelectNone,
    onSelectDefaults,
    onToggleShowAll,
  } = props;
  const hiddenCount = servers.length - visibleServers.length;

  return (
    <section aria-label="Server selection" data-testid="install-selector">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            1. Pick servers
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {selected.size} selected
            {!showAll && hiddenCount > 0 && (
              <> · {hiddenCount} more in the full directory</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <ChipButton onClick={onSelectDefaults}>Defaults</ChipButton>
          <ChipButton onClick={onSelectAll}>All visible</ChipButton>
          <ChipButton onClick={onSelectNone}>None</ChipButton>
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading directory…
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <CategoryGroup
              key={group.category}
              label={CATEGORY_LABEL[group.category] ?? group.category}
              servers={group.servers}
              selected={selected}
              onToggle={onToggle}
            />
          ))}

          <button
            type="button"
            onClick={onToggleShowAll}
            data-testid="install-toggle-all"
            className="w-full rounded-md border border-dashed border-border bg-background px-4 py-3 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            {showAll
              ? `← Show curated 20 only`
              : `Show all ${servers.length} servers (full directory) →`}
          </button>
        </div>
      )}
    </section>
  );
}

function CategoryGroup({
  label,
  servers,
  selected,
  onToggle,
}: {
  label: string;
  servers: DirectoryServer[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}): React.JSX.Element {
  return (
    <div>
      <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <ul className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2">
        {servers.map((s) => (
          <ServerRow
            key={s.key}
            server={s}
            isSelected={selected.has(s.key)}
            onToggle={() => onToggle(s.key)}
          />
        ))}
      </ul>
    </div>
  );
}

function ServerRow({
  server,
  isSelected,
  onToggle,
}: {
  server: DirectoryServer;
  isSelected: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <li className="bg-background">
      <label
        className={cn(
          "flex h-full cursor-pointer items-start gap-3 p-3 transition-colors",
          isSelected ? "bg-emerald-500/5" : "hover:bg-accent",
        )}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="mt-0.5 size-4 cursor-pointer rounded border-border accent-emerald-500"
          data-testid={`install-server-${server.key}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium">{server.name}</span>
            {!server.isCurated && (
              <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                ext
              </span>
            )}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {server.description || server.url}
          </p>
        </div>
      </label>
    </li>
  );
}

function ChipButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border px-2 py-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  );
}

interface OutputProps {
  tab: ClientTab;
  tabs: ClientTab[];
  clientId: ClientId;
  showCli: boolean;
  copyState: CopyState;
  config: string;
  onSelectClient: (id: ClientId) => void;
  onToggleCli: () => void;
  onCopy: () => void;
}

function ConfigOutput(props: OutputProps): React.JSX.Element {
  const {
    tab,
    tabs,
    clientId,
    showCli,
    copyState,
    config,
    onSelectClient,
    onToggleCli,
    onCopy,
  } = props;

  const copyLabel =
    copyState === COPY_STATE.COPIED
      ? "Copied!"
      : copyState === COPY_STATE.FAILED
        ? "Select & copy"
        : "Copy";

  return (
    <section
      aria-label="Generated config"
      data-testid="install-output"
      className="lg:sticky lg:top-20 lg:self-start"
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight">
          2. Pick your client
        </h2>
      </div>

      <div
        role="tablist"
        className="flex flex-wrap gap-1 rounded-md border border-border bg-card p-1"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={clientId === t.id}
            onClick={() => onSelectClient(t.id)}
            data-testid={`install-tab-${t.id}`}
            className={cn(
              "flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors",
              clientId === t.id
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Path:{" "}
            <span className="text-foreground/70">{tab.configPath}</span>
          </p>
          <div className="flex items-center gap-2">
            {tab.hasCli && (
              <button
                type="button"
                onClick={onToggleCli}
                data-testid="install-toggle-cli"
                className="rounded-md border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {showCli ? "JSON" : "CLI"}
              </button>
            )}
            <button
              type="button"
              onClick={onCopy}
              disabled={!config}
              data-testid="install-copy"
              className={cn(
                "rounded-md border px-3 py-1 text-[10px] font-medium uppercase tracking-wider transition-colors",
                config
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
                  : "cursor-not-allowed border-border text-muted-foreground/60",
              )}
            >
              {copyLabel}
            </button>
          </div>
        </div>

        <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-card p-4 font-mono text-xs leading-relaxed">
          <code>
            {config ||
              "// Select at least one server on the left to generate config."}
          </code>
        </pre>

        <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
          {tab.notes}
        </div>
      </div>
    </section>
  );
}

function FaqSection(): React.JSX.Element {
  return (
    <section className="mt-16 border-t border-border pt-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <FaqItem
          q="Are these servers public?"
          a="Yes. Every Worker is unauthenticated and rate-limited. There's nothing to log into."
        />
        <FaqItem
          q="What about the portal?"
          a={
            <>
              A single OAuth-protected URL aggregating multiple servers (with
              Code Mode) is in pilot for 3 servers — see{" "}
              <code>scripts/cf-portal/README.md</code>. Useful when you want one
              entry instead of N.
            </>
          }
        />
        <FaqItem
          q="Can I self-host?"
          a={
            <>
              Yes — Apache-2.0, full monorepo on{" "}
              <a
                href="https://github.com/QuentinCody/bio-mcp-client"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                GitHub
              </a>
              . Each server deploys to Cloudflare Workers in one command.
            </>
          }
        />
      </div>
    </section>
  );
}

function FaqItem({
  q,
  a,
}: {
  q: string;
  a: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <p className="text-sm font-semibold tracking-tight">{q}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{a}</p>
    </div>
  );
}

function InstallFooter(): React.JSX.Element {
  return (
    <footer className="border-t border-border">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <div>
          Bio MCP Portal — research console · Apache-2.0 · built on Cloudflare
          Workers
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <a
            href="https://modelcontextprotocol.io"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground"
          >
            About MCP
          </a>
        </div>
      </div>
    </footer>
  );
}
